import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { API, apiClient } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, FileCheck2, CheckCircle2, Download } from "lucide-react";

export default function HealthCheck() {
  const { t, lang } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(t("common.errorMsg"));
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/health-check", { ...form, language: lang });
      setDone(true);
      toast.success(t("tools.health.successTitle"), { description: t("tools.health.successMsg") });
    } catch {
      toast.error(t("common.errorMsg"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="health-check-page">
      <SEO
        title={`Free Facility Health Check (PDF) | ${COMPANY.brand}`}
        description="Download a free 60-point UAE facility health check PDF. Use it as a vendor scorecard or in-house audit checklist."
      />

      <section className="bg-white py-16 lg:py-20">
        <div className="container-orion max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-700">
            <ArrowLeft className="w-4 h-4 rtl-flip" />
            {t("nav.home")}
          </Link>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                  <FileCheck2 className="w-6 h-6" />
                </div>
                <div className="text-eyebrow">{t("tools.sectionLabel")}</div>
              </div>
              <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05] text-slate-900">
                {t("tools.health.title")}
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">{t("tools.health.short")}</p>

              <ul className="mt-10 space-y-4">
                {t("tools.health.bullets").map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                    <span className="text-slate-700 leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-2xl shadow-blue-900/5">
                {!done ? (
                  <form onSubmit={submit} className="space-y-4" data-testid="health-form">
                    <div className="text-eyebrow text-blue-700">{t("tools.health.formHeading")}</div>
                    <p className="text-sm text-slate-600 leading-relaxed mt-2">
                      {t("tools.health.formSub")}
                    </p>
                    <div className="space-y-4 mt-6">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                          {t("contact.formName")}
                        </Label>
                        <Input
                          data-testid="health-name"
                          value={form.name}
                          onChange={update("name")}
                          required
                          className="h-12 rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                          {t("contact.formEmail")}
                        </Label>
                        <Input
                          data-testid="health-email"
                          type="email"
                          value={form.email}
                          onChange={update("email")}
                          required
                          className="h-12 rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                          {t("contact.formPhone")}
                        </Label>
                        <Input
                          data-testid="health-phone"
                          value={form.phone}
                          onChange={update("phone")}
                          required
                          placeholder="+971 ..."
                          className="h-12 rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                          Company
                        </Label>
                        <Input
                          data-testid="health-company"
                          value={form.company}
                          onChange={update("company")}
                          className="h-12 rounded-lg"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      data-testid="health-submit"
                      disabled={submitting}
                      className="btn-primary-orion w-full disabled:opacity-60 mt-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t("common.submitting")}
                        </>
                      ) : (
                        <>
                          {t("tools.health.downloadBtn")}
                          <ArrowRight className="w-4 h-4 rtl-flip" />
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  <div data-testid="health-success" className="fade-up text-center">
                    <div className="mx-auto w-14 h-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center">
                      <CheckCircle2 className="w-7 h-7" />
                    </div>
                    <h3 className="mt-5 font-heading text-2xl tracking-tight font-medium text-slate-900">
                      {t("tools.health.successTitle")}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {t("tools.health.successMsg")}
                    </p>
                    <a
                      data-testid="health-download"
                      href={`${API}/health-check.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-primary-orion w-full mt-7"
                    >
                      <Download className="w-4 h-4" />
                      {t("tools.health.downloadBtn")}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
